import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { fetchUsersAction, updateBrokerRequestAction } from "../../ustils/actioncreator";
import { useAdminUi } from "../../hooks/useAdminUi";

const APPROVAL_ROLE = 2;
const BROKER_FETCH_BATCH = 100;
const MAX_BROKER_FETCH_PAGES = 50;

function isPendingVerification(user) {
  return String(user?.verificationStatus || "").toLowerCase() === "pending";
}

async function fetchAllBrokerUsersForFilter() {
  const merged = [];
  for (let apiPage = 1; apiPage <= MAX_BROKER_FETCH_PAGES; apiPage += 1) {
    const res = await fetchUsersAction({ page: apiPage, perPage: BROKER_FETCH_BATCH, role: APPROVAL_ROLE });
    const list = extractUserList(res);
    merged.push(...list);
    const totalFromApi = extractTotal(res, merged.length);
    if (list.length === 0 || merged.length >= totalFromApi || list.length < BROKER_FETCH_BATCH) {
      break;
    }
  }
  return merged;
}

function toNumber(value, fallback = null) {
  if (value === undefined || value === null || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function extractUserList(payload) {
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

function extractTotal(res, listLength) {
  if (!res || typeof res !== "object") return listLength;
  const n = toNumber(res.totalRecords, null);
  if (n !== null && n >= 0) return n;
  return listLength;
}

function mapRow(user) {
  const name = user?.name || user?.full_name || "—";
  const email = user?.email || user?.professionalEmail || "—";
  const country = user?.countryCode != null ? String(user.countryCode).trim() : "";
  const mobile = user?.mobileNumber || user?.mobile || "";
  const phone = [country, mobile].filter(Boolean).join(" ").trim() || "—";
  const cities = Array.isArray(user?.dealingCities) ? user.dealingCities.filter(Boolean).join(", ") : "";
  const location =
    cities || user?.dealingCountry || user?.location || user?.address || user?.city || "—";
  const dateRaw = user?.created_at || user?.createdAt || user?.updated_at;
  const submitDate = dateRaw
    ? new Date(dateRaw).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "—";
  const id = user?._id || user?.id;
  const verificationStatus = user?.verificationStatus != null ? String(user.verificationStatus) : "—";
  const avatarUrl = user?.userProfileImage || null;
  return { id, name, email, phone, location, submitDate, verificationStatus, avatarUrl, raw: user };
}

export default function UserApprovalsList() {
  const {
    listHeaderSx,
    listPaperSx,
    listSubtitleSx,
    listTitleSx,
    statusChipSx,
    tableBodyCellSx,
    tableHeaderCellSx,
    tableRowHoverSx,
    tableSx,
    tableContainerSx,
  } = useAdminUi();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const allBrokers = await fetchAllBrokerUsersForFilter();
      setPendingUsers(allBrokers.filter(isPendingVerification));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load approvals.");
      setPendingUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const total = pendingUsers.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage) || 1);
  const rows = useMemo(
    () => pendingUsers.slice((page - 1) * perPage, page * perPage).map(mapRow),
    [pendingUsers, page, perPage]
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const canGoNext = page < totalPages;

  const openReject = (id) => {
    setRejectTargetId(id);
    setRejectReason("");
    setActionError("");
    setRejectOpen(true);
  };

  const closeReject = () => {
    setRejectOpen(false);
    setRejectTargetId(null);
    setRejectReason("");
    setActionError("");
  };

  const handleApprove = async (id) => {
    setActionError("");
    setBusyId(id);
    try {
      const out = await updateBrokerRequestAction({ id, status: "approved", rejectionReason: "" });
      if (out?.success === false) {
        setActionError(out?.message || "Approve failed.");
        return;
      }
      await load();
    } catch (e) {
      setActionError(e?.response?.data?.message || e?.message || "Approve failed.");
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async () => {
    if (!rejectTargetId) return;
    const reason = rejectReason.trim();
    if (!reason) {
      setActionError("Please enter a rejection reason.");
      return;
    }
    setActionError("");
    setBusyId(rejectTargetId);
    try {
      const out = await updateBrokerRequestAction({
        id: rejectTargetId,
        status: "rejected",
        rejectionReason: reason,
      });
      if (out?.success === false) {
        setActionError(out?.message || "Reject failed.");
        return;
      }
      closeReject();
      await load();
    } catch (e) {
      setActionError(e?.response?.data?.message || e?.message || "Reject failed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Paper elevation={0} sx={listPaperSx}>
      <Box sx={listHeaderSx}>
        <Typography sx={listTitleSx}>Approval List</Typography>
        <Typography sx={listSubtitleSx}>Broker accounts (role 2) with verification status pending only.</Typography>
      </Box>
      {error ? (
        <Alert severity="error" sx={{ m: 1 }}>
          {error}
        </Alert>
      ) : null}
      {actionError && !rejectOpen ? (
        <Alert severity="error" sx={{ m: 1 }} onClose={() => setActionError("")}>
          {actionError}
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <TableContainer sx={tableContainerSx}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeaderCellSx}>Customer Photo & Name</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Role</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Location</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Email</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Submit Date</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Status</TableCell>
                  <TableCell sx={tableHeaderCellSx}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ ...tableBodyCellSx, textAlign: "center" }}>
                      No pending broker approvals.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow
                      hover
                      key={r.id || r.email}
                      sx={{ cursor: r.id ? "pointer" : "default", ...tableRowHoverSx }}
                      onClick={() => r.id && navigate(`/dashboard/customers/overview/${r.id}/info`)}
                    >
                      <TableCell sx={tableBodyCellSx}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar
                            src={r.avatarUrl || undefined}
                            sx={{ width: 32, height: 32, fontSize: 12, flexShrink: 0 }}
                          >
                            {(r.name || "?")[0]}
                          </Avatar>
                          <Typography sx={{ fontSize: 14 }}>{r.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={tableBodyCellSx}>
                        <Chip label="Broker" size="small" sx={{ height: 26, fontSize: 14 }} />
                      </TableCell>
                      <TableCell sx={tableBodyCellSx}>{r.location}</TableCell>
                      <TableCell sx={tableBodyCellSx}>{r.email}</TableCell>
                      <TableCell sx={tableBodyCellSx}>{r.submitDate}</TableCell>
                      <TableCell sx={tableBodyCellSx}>
                        <Chip
                          label={r.verificationStatus}
                          size="small"
                          sx={{ height: 26, fontSize: 14, ...statusChipSx(r.verificationStatus) }}
                        />
                      </TableCell>
                      <TableCell sx={tableBodyCellSx} onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={busyId === r.id}
                            onClick={() => r.id && handleApprove(r.id)}
                            color="primary"
                            sx={{ textTransform: "none", fontSize: 14 }}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            disabled={busyId === r.id}
                            onClick={() => r.id && openReject(r.id)}
                            sx={{ textTransform: "none", fontSize: 14 }}
                          >
                            Reject
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" justifyContent={{ xs: "center", sm: "flex-end" }} flexWrap="wrap" spacing={1} sx={{ p: 1.2 }}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </Button>
            <Typography sx={{ fontSize: 14, alignSelf: "center" }}>
              Page {page} {total ? `(${total} total)` : ""}
            </Typography>
            <Button size="small" disabled={!canGoNext} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </Stack>
        </>
      )}

      <Dialog open={rejectOpen} onClose={() => !busyId && closeReject()} fullWidth maxWidth="xs">
        <DialogTitle>Reject request</DialogTitle>
        <DialogContent>
          {actionError ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {actionError}
            </Alert>
          ) : null}
          <TextField
            autoFocus
            margin="dense"
            label="Rejection reason"
            fullWidth
            multiline
            minRows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason shown to the user"
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReject} disabled={!!busyId}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={submitReject} disabled={!!busyId}>
            {busyId ? <CircularProgress size={18} color="inherit" /> : "Submit rejection"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
